import React, { useState, useEffect, useRef } from "react";
import { FiChevronLeft, FiChevronRight, FiShoppingBag, FiHeart, FiEye, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";
import { imgurl } from "../baseurl";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import productDefault from "../assets/img/product.webp";
import Wishlist from "./Wishlist";

function ProductSlide({ categoryName, category_slug, products = [], loading = false }) {
  const [current, setCurrent] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isPaused, setIsPaused] = useState(false); // State to pause auto-scroll
  
  const itemsToShow = 5;
  const totalSlides = Math.ceil(products.length / itemsToShow);

  // --- Auto Scroll Logic Start ---
  useEffect(() => {
    if (loading || totalSlides <= 1 || isPaused) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 3000); // 3 seconds

    return () => clearInterval(interval);
  }, [current, loading, totalSlides, isPaused]);
  // --- Auto Scroll Logic End ---

  const nextSlide = () => {
    setCurrent((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrent((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const getDiscount = (price, mrp) => {
    const p = parseFloat(price);
    const m = parseFloat(mrp);
    if (!m || m <= p) return null;
    return Math.round(((m - p) / m) * 100);
  };

  const closeQuickView = () => setSelectedProduct(null);

  return (
    <div className="product-section my-2">
      <div className="container-fluid px-lg-5">
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div className="section-title">
            <span className="text-uppercase tracking-wider text-muted small fw-bold">Selection</span>
            <h3 className="fw-bold m-0 text-dark">{categoryName || <Skeleton width={180} />}</h3>
            <div className="accent-line"></div>
          </div>
          {!loading && (
            <Link to={`/category/${category_slug}`} className="view-all-link">
              Explore All Collection <FiChevronRight className="ms-1" />
            </Link>
          )}
        </div>

        <div 
          className="slider-container-main"
          onMouseEnter={() => setIsPaused(true)}  // Stop auto-scroll on hover
          onMouseLeave={() => setIsPaused(false)} // Resume auto-scroll
        >
          <div
            className="slides-wrapper-pro"
            style={{
              transform: `translateX(-${current * 100}%)`,
              transition: "transform 0.8s cubic-bezier(0.65, 0, 0.35, 1)"
            }}
          >
            {loading
              ? Array.from({ length: 5 }).map((_, idx) => (
                  <div className="product-card-pro" key={idx}>
                    <Skeleton height={240} borderRadius={15} />
                    <Skeleton width="80%" style={{ marginTop: 12 }} />
                    <Skeleton width="40%" />
                  </div>
                ))
              : products.map((p, idx) => {
                  const discount = getDiscount(p.price, p.mrp);
                  const savings = p.mrp - p.price;

                  return (
                    <div className="product-card-pro" key={idx}>
                      <div className="card-inner shadow-sm">
                        {discount > 0 && (
                          <div className="badge-discount">
                            <span className="discount-value">{discount}%</span>
                            <span className="discount-label">OFF</span>
                          </div>
                        )}
                        
                        <div className="card-actions">
                          <Wishlist productId={p.id} />
                          <button 
                            className="action-btn" 
                            title="Quick View" 
                            onClick={() => setSelectedProduct(p)}
                          >
                            <FiEye />
                          </button>
                        </div>

                        <Link to={`/product/${p.slug}`} className="image-box">
                          <img
                            src={p.featured_image ? `${imgurl}${p.featured_image}` : productDefault}
                            alt={p.name}
                            loading="lazy"
                            onError={(e) => { e.target.src = productDefault; }}
                          />
                        </Link>

                        <div className="info-box">
                          <p className="sub-cat">{p.subcategory_name || 'Limited Edition'}</p>
                          <Link to={`/product/${p.slug}`} className="product-name">
                            {p.name}
                          </Link>
                          
                          <div className="price-container">
                            <div className="d-flex align-items-center gap-2">
                              <span className="curr-price">₹{p.price}</span>
                              {discount > 0 && <span className="old-price">₹{p.mrp}</span>}
                            </div>
                            
                            {discount > 0 && (
                              <div className="savings-tag">
                                You Save ₹{savings.toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
          </div>

          {!loading && totalSlides > 1 && (
            <>
              <button className="nav-btn-pro prev" onClick={prevSlide}><FiChevronLeft /></button>
              <button className="nav-btn-pro next" onClick={nextSlide}><FiChevronRight /></button>
            </>
          )}
        </div>
      </div>

      {/* Quick View Modal Overlay */}
      {selectedProduct && (
        <div className="qv-overlay" onClick={closeQuickView}>
          <div className="qv-modal shadow-lg" onClick={(e) => e.stopPropagation()}>
            <button className="qv-close-btn" onClick={closeQuickView}><FiX /></button>
            <div className="row g-0">
              <div className="col-md-6 qv-img-container">
                <img 
                   src={selectedProduct.featured_image ? `${imgurl}${selectedProduct.featured_image}` : productDefault} 
                   alt={selectedProduct.name} 
                />
              </div>
              <div className="col-md-6 qv-content">
                <p className="qv-sub-cat">{selectedProduct.subcategory_name || 'Limited Edition'}</p>
                <h2 className="qv-name">{selectedProduct.name}</h2>
                <div className="qv-price-box my-3">
                   <span className="qv-curr-price">₹{selectedProduct.price}</span>
                   {getDiscount(selectedProduct.price, selectedProduct.mrp) > 0 && (
                     <span className="qv-old-price ms-2">₹{selectedProduct.mrp}</span>
                   )}
                </div>
                <div className="qv-desc">
                   <p className="text-muted small">
                    {selectedProduct.description ? 
                      selectedProduct.description.replace(/<[^>]*>/g, '').substring(0, 200) + "..." : 
                      "High-quality product curated just for you. Add this to your collection today."}
                   </p>
                </div>
                <div className="qv-footer mt-4">
                  <Link 
                    to={`/product/${selectedProduct.slug}`} 
                    className="btn btn-dark w-100 py-3 fw-bold"
                    onClick={closeQuickView}
                  >
                    View Full Product Details
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Quick View Modal Styles */
        .qv-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.6); backdrop-filter: blur(4px);
          z-index: 9999; display: flex; align-items: center; justify-content: center;
          padding: 20px;
        }
        .qv-modal {
          background: #fff; width: 100%; max-width: 800px; border-radius: 20px;
          position: relative; overflow: hidden; animation: qvPop 0.3s ease-out;
        }
        @keyframes qvPop {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .qv-close-btn {
          position: absolute; top: 15px; right: 15px; background: #fff;
          border: 1px solid #eee; width: 35px; height: 35px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center; z-index: 10;
        }
        .qv-img-container { background: #f9fafb; display: flex; align-items: center; justify-content: center; padding: 12px; }
        .qv-img-container img { max-width: 100%; height: 320px; object-fit: contain; }
        .qv-content { padding: 40px; display: flex; flex-direction: column; justify-content: center; }
        .qv-sub-cat { font-size: 0.75rem; font-weight: 700; color: #ff4757; text-transform: uppercase; margin-bottom: 5px; }
        .qv-name { font-size: 1.5rem; font-weight: 800; color: #1a202c; line-height: 1.2; }
        .qv-curr-price { font-size: 2rem; font-weight: 900; color: #1a202c; }
        .qv-old-price { font-size: 1.1rem; text-decoration: line-through; color: #cbd5e0; }

        @media (max-width: 768px) {
          .qv-img-container { padding: 15px; }
          .qv-img-container img { height: 200px; }
          .qv-content { padding: 20px; }
          .qv-name { font-size: 1.2rem; }
        }

        .tracking-wider { letter-spacing: 1.5px; }
        .accent-line { width: 40px; height: 3px; background: #ff4757; margin-top: 8px; border-radius: 10px; }
        .view-all-link {
          text-decoration: none; color: #1a1a1a; font-weight: 700; font-size: 0.85rem;
          background: #fff; padding: 10px 20px; border-radius: 50px; border: 1px solid #eee;
          transition: 0.3s ease;
        }
        .view-all-link:hover { background: #ff4757; color: #fff; border-color: #ff4757; transform: translateX(5px); }
        .slider-container-main { position: relative; overflow: hidden; padding: 20px 0; }
        .slides-wrapper-pro { display: flex; }
        .product-card-pro { flex: 0 0 20%; padding: 5px; box-sizing: border-box; }
        .card-inner {
          background: #fff; border-radius: 20px; overflow: hidden; position: relative;
          height: 100%; border: 1px solid rgba(214, 214, 214, 0.83); 
          transition: 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .badge-discount {
          position: absolute; top: 15px; left: 15px; z-index: 5;
          background: #ed712f; color: #fff; padding: 5px 12px;
          border-radius: 12px; display: flex; flex-direction: column; align-items: center;
          line-height: 1; box-shadow: 0 4px 10px rgba(255, 71, 87, 0.4);
        }
        .discount-value { font-size: 0.85rem; font-weight: 900; }
        .discount-label { font-size: 0.5rem; font-weight: 700; }
        .image-box { display: flex; align-items: center; justify-content: center; position: relative; height: 240px; overflow: hidden; background: #f9fafb; }
        .image-box img { width: 85%; height: 85%; object-fit: contain; transition: 0.8s ease; }
       
        .card-actions { position: absolute; top: 15px; right: -50px; display: flex; flex-direction: column; gap: 10px; transition: 0.3s ease; z-index: 10; }
        .card-inner:hover .card-actions { right: 15px; }
        .action-btn { 
            width: 40px; height: 40px; border-radius: 50%; background: #636363; border: none; 
            display: flex; align-items: center; justify-content: center; box-shadow: 0 5px 15px rgba(0,0,0,0.08);
            color: #f5f5f5; transition: 0.3s;
        }
        .action-btn:hover { background: #ed712f; color: #fff; transform: scale(1.1); }
        .info-box { padding: 10px; }
        .sub-cat { font-size: 0.65rem; font-weight: 500; color: #3d3d3d; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; }
        .product-name { 
          font-size: .89rem; font-weight: 600; color: #1a202c; 
          text-decoration: none; display: -webkit-box; -webkit-line-clamp: 2; 
          -webkit-box-orient: vertical; overflow: hidden; margin-bottom: 5px;
        }
        .price-container { display: flex; flex-direction: column; gap: 4px; }
        .curr-price { font-size: 1.25rem; font-weight: 900; color: #ed712f; letter-spacing: -0.5px; }
        .old-price { font-size: 0.9rem; text-decoration: line-through; color: #585858; font-weight: 600; }
        .savings-tag { 
          font-size: 0.7rem; color: #38a169; font-weight: 800; background: #f0fff4; 
          padding: 3px 8px; border-radius: 6px; width: fit-content; border: 1px solid #c6f6d5;
        }
        .nav-btn-pro {
          position: absolute; top: 50%; transform: translateY(-50%);
          width: 50px; height: 50px; background: #fff; border: none;
          border-radius: 50%; box-shadow: 0 10px 25px rgba(0,0,0,0.1);
          z-index: 20; transition: 0.4s; display: flex; align-items: center; justify-content: center; color: #2d3436;
        }
        .nav-btn-pro:hover { background: #1a202c; color: #fff; transform: translateY(-50%) scale(1.1); }
        .prev { left: -12px !important; }
        .next { right: -12px !important; }

        @media (max-width: 768px) {
          .nav-btn-pro { display: none; }
          .slider-container-main { overflow-x: auto; padding-left: 15px; scrollbar-width: none; }
          .slides-wrapper-pro { transform: none !important; }
          .product-card-pro { flex: 0 0 78%; padding-right: 15px; }
          .card-actions { right: 15px; }
          .image-box { height: 200px; }
        }
      `}</style>
    </div>
  );
}

export default ProductSlide;